import React, { Component } from 'react';
import _sortBy from 'lodash/sortBy';
import { string, arrayOf, func, shape, bool } from 'prop-types';
import cx from 'classnames';
import I18n from 'i18n-js';
import { connect } from 'react-redux';
import { isEmpty } from 'common/utils/data-utils';
import SelectField from 'components/form-field/select-field/select-field';
import {
  DOODLE_VALUE,
  FIELD_PLACEHOLDER,
  VALIDATE_MESSAGE,
  CASH,
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  ADD_CASHLESS_ON_APP,
} from 'constants/booking-constants';
import _get from 'lodash/get';
import {
  getCreditCardPaymentMethods,
  getGrabPayPaymentMethod,
  getPostpaidPaymentMethod,
  getGrabPayBalanceDisplay,
} from 'helper/payment';
import { getUserCountryCode } from 'helper/user';
import { getAllPaxPaymentMethodsV2 } from 'actions/booking-actions';
import { withCashlessPaymentDisabledPopover } from 'components/hoc/with-cashless-disabled-popover';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import infoIcon from 'public/icons/info.svg';
import paymentIcon from './payment-icon';
import css from './payment-field.less';

class PaymentField extends Component {
  failedPaymentPopoverOptions = (child, onRetry) => ({
    child,
    onRetry,
    message: I18n.t('v2.failedPayments.bookingPopoverText'),
    buttonText: I18n.t('v2.failedPayments.bookingPopoverButton'),
    placement: 'right',
  });
  failedGrabPayPopoverOptions = (child, onRetry) => ({
    child,
    onRetry,
    message: I18n.t('v2.failedPayments.grabPay.popOverText'),
    buttonText: I18n.t('v2.failedPayments.grabPay.bookingPopoverButton'),
    placement: 'right',
  });
  onRetryGrabPayPayment = () => {
    const { onRetryGrabPay } = this.props;
    const countryCode = getUserCountryCode();
    onRetryGrabPay(countryCode);
  };
  renderPaymentMethodOptions = (
    paymentMethods,
    disabled,
    allowCash,
    allowCard,
    postPaidPaymentMethod,
    grabPayPaymentMethod,
    currentSelectedPayment,
    isSameDayServiceSelected,
    isCashPaymentDisabled,
    isCashlessPaymentDisabled,
    onRetryFailedPayments,
    totalAmount,
  ) => {
    if (disabled) {
      return [
        {
          title: this.props.label,
          value: isEmpty(currentSelectedPayment)
            ? DOODLE_VALUE
            : currentSelectedPayment,
          child: (
            <div className={css.paymentOption}>
              <img src={paymentIcon[CASH]} alt="" />
              <h6>{I18n.t(FIELD_PLACEHOLDER.CASH)}</h6>
            </div>
          ),
        },
      ];
    }

    const CASH_PAYMENT_OPTIONS = [
      {
        value: CASH_BY_SENDER,
        title: I18n.t('booking.paymentMethod.cashBySender'),
        child: (
          <div className={css.paymentOption}>
            <img src={paymentIcon[CASH]} alt="" width={30} />
            <h6>{I18n.t('booking.paymentMethod.cashBySender')}</h6>
          </div>
        ),
        disabled: false,
      },
      {
        value: CASH_BY_RECIPIENT,
        title: I18n.t('booking.paymentMethod.cashByRecipient'),
        child: (
          <div className={css.paymentOption}>
            <img src={paymentIcon[CASH]} alt="" width={30} />
            <h6>{I18n.t('booking.paymentMethod.cashByRecipient')}</h6>
          </div>
        ),
        disabled: false,
      },
    ];
    const ADD_CASHLESS_ON_APP_OPTION = {
      value: ADD_CASHLESS_ON_APP,
      title: I18n.t('booking.paymentMethod.addCashlessPayments'),
      child: (
        <div className={css.paymentOptionInfo}>
          <img src={infoIcon} alt="" width={30} />
          <h6>{I18n.t('booking.paymentMethod.addCashlessPayments')}</h6>
        </div>
      ),
      disabled: true,
    };
    const paymentMethodOptions = [];
    let hasCashlessPaymentAvail = false;
    const postpaidBillingDisabled =
      !_get(postPaidPaymentMethod, 'enabled') || isCashlessPaymentDisabled;
    const grabPayPaymentDisabled =
      !_get(grabPayPaymentMethod, 'enabled') || isCashlessPaymentDisabled;

    if (postPaidPaymentMethod) {
      const postpaidBillingOptionContents = (
        <div
          className={cx(css.paymentOption, {
            [css.paymentOptionDisabled]: postpaidBillingDisabled,
          })}
        >
          <img
            src={paymentIcon[postPaidPaymentMethod.type]}
            alt=""
            width={30}
          />
          <h6>{I18n.t(FIELD_PLACEHOLDER.CORP_BILLING)}</h6>
        </div>
      );

      const popoverOptions = this.failedPaymentPopoverOptions(
        postpaidBillingOptionContents,
        onRetryFailedPayments,
      );

      if (!postpaidBillingDisabled) {
        hasCashlessPaymentAvail = true;
      }
      paymentMethodOptions.push({
        value: postPaidPaymentMethod.paymentTypeID.toString(),
        title: I18n.t(FIELD_PLACEHOLDER.CORP_BILLING),
        child: postpaidBillingDisabled
          ? withCashlessPaymentDisabledPopover(popoverOptions)
          : postpaidBillingOptionContents,
        disabled: postpaidBillingDisabled,
      });
    }

    if (grabPayPaymentMethod) {
      const isNotValidGrabPayBalance =
        _get(grabPayPaymentMethod, 'payload.Balance', 0) < totalAmount;

      const grabPayOptionContents = (
        <div
          className={cx(css.paymentOption, {
            [css.paymentOptionDisabled]:
              grabPayPaymentDisabled || isNotValidGrabPayBalance,
          })}
        >
          <img src={paymentIcon.grabpay} alt="" width={30} />
          <h6>{getGrabPayBalanceDisplay(grabPayPaymentMethod)}</h6>
        </div>
      );

      const grabPayPopoverOptions = this.failedGrabPayPopoverOptions(
        grabPayOptionContents,
        this.onRetryGrabPayPayment,
      );

      if (!grabPayPaymentDisabled) {
        hasCashlessPaymentAvail = true;
      }
      paymentMethodOptions.push({
        value: grabPayPaymentMethod.paymentTypeID.toString(),
        title: I18n.t(FIELD_PLACEHOLDER.GRAB_PAY),
        child: grabPayPaymentDisabled
          ? withCashlessPaymentDisabledPopover(grabPayPopoverOptions)
          : isNotValidGrabPayBalance
            ? withCashlessPaymentDisabledPopover(grabPayPopoverOptions)
            : grabPayOptionContents,
        disabled: grabPayPaymentDisabled || isNotValidGrabPayBalance,
      });
    }

    if (allowCard) {
      getCreditCardPaymentMethods(paymentMethods).forEach(payment => {
        const { type, refNumber } = payment;

        const cardPaymentDisabled =
          !payment.enabled || isCashlessPaymentDisabled;

        const optionContents = (
          <div
            className={cx(css.paymentOption, {
              [css.paymentOptionDisabled]: cardPaymentDisabled,
            })}
          >
            <img
              src={paymentIcon[type] || paymentIcon['0']}
              alt=""
              width={30}
            />
            <h6>{refNumber}</h6>
          </div>
        );

        const popoverOptions = this.failedPaymentPopoverOptions(
          optionContents,
          onRetryFailedPayments,
        );

        // If card is not disabled, set the variable to true
        if (!cardPaymentDisabled) {
          hasCashlessPaymentAvail = true;
        }
        paymentMethodOptions.push({
          value: payment.paymentTypeID.toString(),
          title: refNumber,
          child: cardPaymentDisabled
            ? withCashlessPaymentDisabledPopover(popoverOptions)
            : optionContents,
          disabled: cardPaymentDisabled,
        });
      });
    }

    if (allowCash && (isSameDayServiceSelected || !isCashPaymentDisabled)) {
      paymentMethodOptions.push(...CASH_PAYMENT_OPTIONS);
    }

    if (isPaxUser() && !hasCashlessPaymentAvail) {
      paymentMethodOptions.push(ADD_CASHLESS_ON_APP_OPTION);
    }

    // sort so that enabled payment methods are on top
    return _sortBy(paymentMethodOptions, p => p.disabled);
  };

  renderPaymentAvailability = (
    allowCard,
    allowCash,
    isSameDayServiceSelected,
  ) => {
    const { serviceTypes, paymentMethods } = this.props;
    if (
      allowCard &&
      !allowCash &&
      serviceTypes.length > 0 &&
      paymentMethods.length === 0 &&
      !isSameDayServiceSelected
    ) {
      return (
        <span className={css.noPaymentNote}>
          {I18n.t(VALIDATE_MESSAGE.PAYMENT_NOT_FOUND)}
        </span>
      );
    } else if (
      allowCard &&
      !allowCash &&
      serviceTypes.length > 0 &&
      isEmpty(paymentMethods)
    ) {
      return (
        <span className={css.noPaymentNote}>
          {I18n.t(VALIDATE_MESSAGE.PAYMENT_NOT_FOUND)}
        </span>
      );
    }
    return null;
  };

  render() {
    const {
      id,
      label,
      paymentMethods,
      allowCash,
      allowCard,
      disabled: propDisabled,
      form,
      className,
      optionClassName,
      dropdownClassName,
      onSelect,
      form: { getFieldValue },
      isSameDayServiceSelected,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      initialValue,
      onRetryFailedPayments,
      totalAmount,
    } = this.props;
    const currentSelectedPayment = getFieldValue(id);
    const postPaidPaymentMethod = getPostpaidPaymentMethod(paymentMethods);
    const grabPayPaymentMethod = getGrabPayPaymentMethod(paymentMethods);
    const disabled =
      propDisabled ||
      (!allowCash && paymentMethods.length === 0) ||
      (!allowCash &&
        !allowCard &&
        !postPaidPaymentMethod &&
        !grabPayPaymentMethod);
    const paymentOptions = this.renderPaymentMethodOptions(
      paymentMethods,
      disabled,
      allowCash,
      allowCard,
      postPaidPaymentMethod,
      grabPayPaymentMethod,
      currentSelectedPayment,
      isSameDayServiceSelected,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      onRetryFailedPayments,
      totalAmount,
    );

    return (
      <div>
        <SelectField
          id={id}
          label={label}
          options={paymentOptions}
          initialValue={initialValue || DOODLE_VALUE}
          disabled={disabled}
          className={cx(css.paymentField, className)}
          optionClassName={optionClassName}
          dropdownClassName={cx(css.paymentDropDown, dropdownClassName)}
          onSelect={onSelect}
          form={form}
        />
        {this.renderPaymentAvailability(
          allowCard,
          allowCash,
          isSameDayServiceSelected,
        )}
      </div>
    );
  }
}

PaymentField.propTypes = {
  id: string.isRequired,
  label: string.isRequired,
  disabled: bool,
  allowCash: bool,
  allowCard: bool,
  paymentMethods: arrayOf(shape({})),
  serviceTypes: arrayOf(shape({})),
  onSelect: func,
  onRetryFailedPayments: func,
  form: shape({}).isRequired,
  className: string,
  dropdownClassName: string,
  optionClassName: string,
  isSameDayServiceSelected: bool.isRequired,
  isCashPaymentDisabled: bool.isRequired,
  isCashlessPaymentDisabled: bool.isRequired,
  initialValue: string, // only use for ui v2
  totalAmount: string,
  onRetryGrabPay: func,
};

PaymentField.defaultProps = {
  disabled: false,
  paymentMethods: [],
  allowCash: false,
  allowCard: false,
  className: null,
  dropdownClassName: null,
  optionClassName: null,
  onSelect: undefined,
  serviceTypes: [],
  isCashPaymentDisabled: false,
  initialValue: null,
  onRetryFailedPayments: null,
  totalAmount: null,
  onRetryGrabPay: null,
};

const mapDispatchToProps = {
  onRetryGrabPay: getAllPaxPaymentMethodsV2,
};

export default connect(null, mapDispatchToProps)(PaymentField);
